import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaEye,
  FaCheck,
  FaSpinner,
  FaEyeSlash,
  FaCheckCircle,
} from "react-icons/fa";
import ApiEndPoints from "../../ServicesCalls/ApiEndPoints";

const getPasswordRules = (password) => ({
  hasMinLength: password.length >= 8,
  hasLower: /[a-z]/.test(password),
  hasUpper: /[A-Z]/.test(password),
  hasNumber: /\d/.test(password),
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromUrl = queryParams.get("email") || "";

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [message, setMessage] = useState({ text: "", color: "" });
  const [passwords, setPasswords] = useState({ new: "", confirm: "" });
  const [visibility, setVisibility] = useState({ new: false, confirm: false });

  const rules = getPasswordRules(passwords.new);

  const renderPasswordField = (type, label) => (
    <div className="relative">
      <input
        type={visibility[type] ? "text" : "password"}
        placeholder={`${label}*`}
        className="w-full px-3 py-2 pl-3 pr-10 mt-2 border border-gray-300 rounded"
        value={passwords[type]}
        onChange={(e) => {
          setPasswords({ ...passwords, [type]: e.target.value });
          if (type === "new") setPasswordTouched(true);
        }}
        required
      />
      <span
        className="absolute top-0 flex items-center h-full text-gray-500 cursor-pointer right-3"
        onClick={() =>
          setVisibility((prev) => ({ ...prev, [type]: !prev[type] }))
        }
      >
        {visibility[type] ? <FaEyeSlash /> : <FaEye />}
      </span>
    </div>
  );

  const renderPasswordRules = () => (
    <div className="px-3 py-2 mt-2 text-sm text-left text-gray-700 border rounded">
      <p className="mb-1 font-semibold">Your password must contain:</p>
      <ul className="space-y-1 list-none">
        {[
          { label: "At least 8 characters", valid: rules.hasMinLength },
          { label: "Lower case letters (a-z)", valid: rules.hasLower },
          { label: "Upper case letters (A-Z)", valid: rules.hasUpper },
          { label: "Numbers (0-9)", valid: rules.hasNumber },
        ].map((rule, idx) => (
          <li key={idx} className="flex items-center">
            {rule.valid ? (
              <FaCheck className="mr-2 text-green-600" />
            ) : (
              <span className="mr-2 text-gray-600">•</span>
            )}
            <span className={rule.valid ? "text-green-600" : ""}>
              {rule.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  const fetchChangePassword = async () => {
    try {
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify({
          Email: emailFromUrl,
          NewPassword: passwords.confirm,
        }),
      };
      const response = await fetch(
        ApiEndPoints.postLoginForgotPassword(),
        requestHeaders
      );
      const data = await response.json();
      const dataSuccess = data?.message;
      const dataError = data?.error;

      if (!response.ok)
        throw new Error(
          dataSuccess || dataError || "Failed to Fetch FortgotPasword API"
        );

      setMessage({ text: dataSuccess, color: "green" });
      setShowSuccess(true);
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setMessage({ text: errorMsg, color: "red" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: "", color: "" });
    fetchChangePassword();
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md p-8 space-y-6 text-center bg-white rounded-lg shadow-md">
        {showSuccess ? (
          <>
            <div className="flex justify-center">
              <FaCheckCircle className="w-16 h-16 mx-auto text-green-700" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Password Changed!
            </h2>
            <p className="text-sm text-gray-600">
              Your password has been changed successfully.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="w-full px-4 py-2 mt-2 font-medium text-white bg-blue-700 rounded hover:bg-blue-800"
            >
              Back to website
            </button>
          </>
        ) : (
          <>
            {message.text && (
              <p
                className={`mt-4 text-sm text-center ${
                  message.color === "green"
                    ? "text-green-600"
                    : message.color === "red"
                    ? "text-red-600"
                    : ""
                }`}
              >
                {message.text}
              </p>
            )}
            {isSubmitting && (
              <div className="flex items-center justify-center gap-2 mt-4 text-gray-700">
                <FaSpinner
                  size={20}
                  className="w-6 h-6 text-blue-500 animate-spin"
                />
                <span>Please wait...</span>
              </div>
            )}
            <form
              onSubmit={handleChangePassword}
              className="w-full max-w-md p-8 space-y-6 text-center bg-white rounded-lg shadow-md"
            >
              <h2 className="text-xl font-semibold text-gray-800">
                Change Your Password
              </h2>
              {renderPasswordField("new", "New password")}
              {renderPasswordField("confirm", "Re-enter new password")}
              {passwordTouched && renderPasswordRules()}
              {passwords.confirm && passwords.new !== passwords.confirm && (
                <p className="mt-1 text-sm text-red-600">
                  New and Confirm Password should match.
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-4 py-2 mt-4 font-medium text-white rounded ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-800"
                }`}
              >
                {isSubmitting ? "Changing password..." : "Change password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
